import React from "react";
import { ExternalLink, Upload } from "lucide-react";
import styles from "./UserCvSection.module.scss";
import {
  formatUserCvUpdatedAt,
  getCvFileNameFromUrl,
  type UserCvProfile,
} from "../../../../utils/userCv.ts";

const ACCEPTED_CV_TYPES = ".pdf,.doc,.docx";

interface UserCvSectionProps {
  savedCv: UserCvProfile;
  cvFile: File | null;
  cvFileName: string;
  cvVisible: boolean;
  onCvFileChange: (file: File | null, fileName: string) => void;
  onCvVisibleChange: (visible: boolean) => void;
}

const UserCvSection: React.FC<UserCvSectionProps> = ({
  savedCv,
  cvFile,
  cvFileName,
  cvVisible,
  onCvFileChange,
  onCvVisibleChange,
}) => {
  const updatedLabel = formatUserCvUpdatedAt(savedCv.updatedAt);
  const hasPendingFile = Boolean(cvFile);
  const displayUrl = savedCv.url;
  const displayUpdatedLabel =
    hasPendingFile && cvFile
      ? "Se actualizará al guardar los cambios"
      : updatedLabel;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onCvFileChange(file, file.name);
  };

  return (
    <div className={styles.section}>
      <p className={styles.lead}>
        Subí un CV en PDF o Word. Lo usamos por defecto cuando te postulás a una vacante, sin
        volver a adjuntarlo cada vez.
      </p>

      {displayUrl && !hasPendingFile ? (
        <div className={styles.statusCard}>
          <p className={styles.statusTitle}>CV guardado</p>
          <p className={styles.statusMeta}>
            {getCvFileNameFromUrl(displayUrl)}
            {displayUpdatedLabel ? ` · ${displayUpdatedLabel}` : ""}
          </p>
          <a
            href={displayUrl}
            target="_blank"
            rel="noreferrer"
            className={styles.statusLink}
          >
            <ExternalLink size={14} />
            Ver archivo actual
          </a>
        </div>
      ) : hasPendingFile ? (
        <div className={styles.statusCard}>
          <p className={styles.statusTitle}>Archivo seleccionado</p>
          <p className={styles.statusMeta}>{cvFileName}</p>
        </div>
      ) : (
        <div className={styles.statusCard}>
          <p className={styles.statusTitle}>Todavía no subiste un CV</p>
          <p className={styles.statusMeta}>
            Elegí un archivo y guardá los cambios del perfil para conservarlo.
          </p>
        </div>
      )}

      <div className={styles.fileGroup}>
        <span className={styles.label}>
          {displayUrl || hasPendingFile ? "Reemplazar CV" : "Subir CV"}
        </span>
        <div className={styles.fileInputWrapper}>
          <input
            id="profile-cv-upload"
            type="file"
            className={styles.fileInput}
            accept={ACCEPTED_CV_TYPES}
            onChange={handleFileChange}
          />
          <label htmlFor="profile-cv-upload" className={styles.fileTrigger}>
            <Upload size={18} />
            {cvFileName || "Seleccionar archivo (.pdf, .doc, .docx)"}
          </label>
        </div>
      </div>

      <label className={styles.visibility}>
        <input
          type="checkbox"
          className={styles.checkbox}
          checked={cvVisible}
          onChange={(e) => onCvVisibleChange(e.target.checked)}
          disabled={!displayUrl && !hasPendingFile}
        />
        <span className={styles.visibilityText}>
          <span className={styles.visibilityLabel}>Mostrar en mi perfil público</span>
          <p className={styles.visibilityHint}>
            Si está activo, quien visite tu perfil verá un enlace para descargar tu CV en la
            pestaña Resumen.
          </p>
        </span>
      </label>
    </div>
  );
};

export default UserCvSection;
